import cx from 'clsx';
import React, { useState } from 'react';
import { Tooltip } from 'react-tooltip';

import Slider from '@components/common/Slider/Slider';
import DatePicker from '@components/form/DatePicker/DatePicker';
import Switch from '@components/uikit/Switch/Switch';

import useTranslation from '@hooks/useTranslation';

import { useUserProfileQuery } from '@services/user/useUserProfileQuery';

import { selectAppRtl } from '@store/useAppStore';

import type { IFilterLimits } from '../../model/engagementFilter';
import {
  selectEngagementFilterCartRangeValue,
  selectEngagementFilterReportFailure,
  selectEngagementFilterStatesValue,
  useEngagementFilterActions,
} from '../../store/useEngagementFilterStore';
import style from './style.modules.scss';

interface IProps {
  id: string;
  visible: boolean;
  filterLimits: IFilterLimits;
  minTimeCreated?: Date;
  maxTimeCreated?: Date;
  actionListStates: { id: number | string; name: string }[];
  isAllStatusesSelected: boolean;
  setIsOpen: (value: boolean) => void;
}
const AdditionalFilters: React.FC<IProps> = ({
  id,
  visible,
  minTimeCreated,
  maxTimeCreated,
  filterLimits,
  actionListStates,
  isAllStatusesSelected,
  setIsOpen,
}) => {
  const { t } = useTranslation();
  const {
    setEngagementFilterTimeCreatedRangeValue,
    setEngagementFilterCartRangeValue,
    setEngagementFilterStatesValue,
    setEngagementFilterReportFailure,
  } = useEngagementFilterActions();

  const rtl = selectAppRtl();
  const cartRange = selectEngagementFilterCartRangeValue();
  const states = selectEngagementFilterStatesValue();
  const reportedFailure = selectEngagementFilterReportFailure();

  const { data: profileData } = useUserProfileQuery();

  const statesAI = actionListStates.filter((state) => state.id !== 5);

  const [calendarOpen, setCalendarOpen] = useState(false);

  const onChangeStatuses = (value: number | string) =>
    setEngagementFilterStatesValue(
      states.includes(value) ? states.filter((v: any) => v !== value) : [...states, value],
    );

  const onChangeDateRange = (dates: [Date | null, Date | null]) => {
    setEngagementFilterTimeCreatedRangeValue({
      minTimeCreated: dates[0],
      maxTimeCreated: dates[1],
    });
  };

  return (
    <Tooltip
      id={id}
      place="bottom-end"
      clickable
      className={style.additionalFilters}
      disableStyleInjection
      setIsOpen={(open) => {
        if (!open && calendarOpen) {
          return;
        }
        setIsOpen(open);
      }}
      openOnClick
      isOpen={visible}
      render={() => {
        return (
          <div className={style.tooltipContainer}>
            <div className={style.tooltipTitle}>{t('Additional Filters')}</div>
            <div className={style.filters}>
              <div className={style.smallWidthShow}>
                <DatePicker
                  id="daterange-engagement-additional"
                  label={t('Action Item Update Date Range')}
                  placeholderText={t('Select Date Range')}
                  dateFormat={rtl ? 'yyyy/MM/dd' : `${profileData?.dateFormat.date}`}
                  startDate={minTimeCreated}
                  endDate={maxTimeCreated}
                  minDate={filterLimits.minTimeCreated}
                  selectsRange
                  showMonthDropdown
                  showYearDropdown
                  autoComplete="off"
                  type="woHelperText"
                  onChange={onChangeDateRange}
                  onCalendarOpen={() => setCalendarOpen(true)}
                  onCalendarClose={() => setCalendarOpen(false)}
                />
              </div>
              <div className={style.smallWidthShow}>
                <Switch
                  value={!!reportedFailure}
                  onChange={() => setEngagementFilterReportFailure(!reportedFailure)}
                  label={t('Customer - Reported Failures')}
                  type="woHelperText"
                />
              </div>
              <div className={style.slideRangeFieldWrapper}>
                <Slider
                  label={t('Customer Average Response Time')}
                  from={+(cartRange.minCart || filterLimits.minCart || 0)}
                  to={+(cartRange.maxCart || filterLimits.maxCart || 0)}
                  onChange={([left, right]) => {
                    setEngagementFilterCartRangeValue({
                      minCart: left,
                      maxCart: right,
                    });
                  }}
                  min={+filterLimits.minCart}
                  max={+filterLimits.maxCart}
                  showToCurrency={false}
                />
              </div>
              <div className={style.statusesControl}>
                <label className={style.fieldLabel}>{t('Statuses')}</label>
                <div className={style.statusesControlItems}>
                  <div
                    className={cx(style.statusesControlItem, {
                      [style.activeStatusesControlItem]: isAllStatusesSelected,
                    })}
                    onClick={() =>
                      isAllStatusesSelected
                        ? setEngagementFilterStatesValue([])
                        : setEngagementFilterStatesValue(actionListStates.map((s) => s.id))
                    }
                  >
                    {t('Select all')}
                  </div>
                  {statesAI.map((state) => (
                    <div
                      key={state.id}
                      className={cx(style.statusesControlItem, {
                        [style.activeStatusesControlItem]: states.includes(state.id),
                      })}
                      onClick={() => onChangeStatuses(state.id)}
                    >
                      {state.name.toLowerCase()}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );
      }}
    />
  );
};
export default AdditionalFilters;
